import { ToolDefinition } from "../types";

/**
 * Navigation tool: lets Kate hand off the user to a relevant Telemetry page.
 * The actual `history.push` happens inside the React component, where the
 * router is in scope. The tool dispatcher passes the requested path back to
 * the renderer via a custom event so the panel component can act on it.
 */

const ALLOWED_PATHS = [
  "/instruments",
  "/runs",
  "/spatialRuns",
  "/customerRuns",
  "/customerSpatialRuns",
  "/customer-map",
  "/customer-calendar",
  "/install-report",
  "/executive-dashboard",
  "/executive-utilization",
  "/executive-internal-utilization",
  "/cyto-qc-dashboard",
] as const;

export const navigationToolDefinitions: ToolDefinition[] = [
  {
    name: "navigate_to",
    description:
      "Navigate the user's browser to one of Telemetry's known pages. " +
      "Use this when the user asks to 'show me X' or 'open Y'. The path must " +
      "be one of the allowed Telemetry routes.",
    input_schema: {
      type: "object",
      properties: {
        path: {
          type: "string",
          description: `Allowed paths: ${ALLOWED_PATHS.join(", ")}`,
        },
        reason: {
          type: "string",
          description:
            "A short, human-readable explanation of why you're navigating " +
            "(shown to the user as a confirmation).",
        },
      },
      required: ["path"],
    },
  },
];

export async function executeNavigateTo(
  input: Record<string, unknown>,
): Promise<unknown> {
  const path = typeof input.path === "string" ? input.path : null;
  const reason = typeof input.reason === "string" ? input.reason : "";
  if (!path) {
    return { success: false, error: "path is required" };
  }
  if (!ALLOWED_PATHS.includes(path as typeof ALLOWED_PATHS[number])) {
    return {
      success: false,
      error: `Path '${path}' is not allowed`,
      allowedPaths: ALLOWED_PATHS,
    };
  }

  window.dispatchEvent(
    new CustomEvent("kate-navigate", { detail: { path, reason } }),
  );

  return { success: true, navigatedTo: path, reason };
}
